import { FC, lazy, useEffect, useState } from 'react'

import * as Sentry from '@sentry/react'
import { useSearchParams } from 'react-router-dom'

import { CredentialsForm, CredentialsFormValues } from './CredentialsForm'
import { MfaForm, MfaFormValues } from './MfaForm'
import { StorageAccessRequestButton } from './StorageAccessRequestButton'
import { AlertError, ErrorProps } from '../../components/AlertError'
import { hasStorageAccess } from '../../services/accessStorage'
import { SignInOptions } from '../../types/SignInOptions'
import {
  ErrorCodes,
  FailedAuthenticationError,
  MfaRequiredError,
  TooManyAttemptsError,
  UserFacingError,
} from '../../utils/errors'
import { QueryParameters } from '../../utils/queryParameters'

const Layout = lazy(() =>
  import('../../components/Shared/Layout').then((module) => ({
    default: module.Layout,
  })),
)

type SignInPageProps = {
  initialShowAuthenticationCodeForm?: MfaRequiredError | null
  initialSignInOptions?: SignInOptions
  initialGlobalErrorMessage?: ErrorProps | null
  onSubmit: (signInOptions: SignInOptions) => Promise<void>
  enableSso: boolean
}

export const SignInPage: FC<SignInPageProps> = ({
  initialShowAuthenticationCodeForm = null,
  initialSignInOptions = { username: '', password: '' },
  initialGlobalErrorMessage = null,
  onSubmit,
  enableSso,
}: SignInPageProps): JSX.Element | null => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showAuthenticationCodeForm, setShowAuthenticationCodeForm] = useState(initialShowAuthenticationCodeForm)
  const [signInOptions, setSignInOptions] = useState(initialSignInOptions)
  const [globalErrorMessage, setGlobalErrorMessage] = useState(initialGlobalErrorMessage)
  const [showStorageAccessRequest, setShowStorageAccessRequest] = useState(false)

  const handleError = (error: Error | null): void => {
    if (error === null) {
      setGlobalErrorMessage(null)
      return
    }
    if (error instanceof MfaRequiredError) {
      const mfaError = new MfaRequiredError(error.channel, error.recipient)
      setShowAuthenticationCodeForm(mfaError)
      return
    }

    if (error instanceof TooManyAttemptsError) {
      setGlobalErrorMessage({ title: '', message: 'sign_in_page.form.error.too_many_attempts' })
      return
    }

    if (error instanceof UserFacingError) {
      setGlobalErrorMessage({ title: error.titleKey, message: error.messageKey })
      return
    }

    setGlobalErrorMessage({ title: 'global.error_title', message: 'global.error_message' })
    throw error
  }

  const handleSubmitError = (error: Error, messageKey: string): void => {
    const submitError = error instanceof FailedAuthenticationError ? new UserFacingError('', messageKey) : error
    handleError(submitError)
  }

  // We pass credentials in params for Formik to avoid manually managing the isSubmitting.
  const submitSignIn = async (credentials: SignInOptions): Promise<void> => {
    setGlobalErrorMessage(null)
    searchParams.delete(QueryParameters.ERROR_CODE)
    setSearchParams(searchParams)

    await onSubmit(credentials)
  }

  const onCredentialsFormSubmit = async (values: CredentialsFormValues): Promise<void> => {
    const newSignInOptions = { username: values.username, password: values.password, isSso: values.isSso }
    setSignInOptions(newSignInOptions)
    try {
      await submitSignIn(newSignInOptions)
    } catch (error) {
      handleSubmitError(error as Error, 'sign_in_page.form.error.wrong_authentication')
    }
  }

  const onMfaFormSubmit = async (values: MfaFormValues): Promise<void> => {
    const newSignInOptions = {
      ...signInOptions,
      authenticationCode: values.authenticationCode,
      secondaryFactor: values.secondaryFactor,
    }
    setSignInOptions(newSignInOptions)
    try {
      await submitSignIn(newSignInOptions)
    } catch (error) {
      handleSubmitError(error as Error, 'sign_in.form.mfa.error.wrong_authentication_code')
    }
  }

  useEffect(() => {
    // Get server errors from URL
    const errorCode = searchParams.get(QueryParameters.ERROR_CODE)
    if (errorCode?.length) {
      if (errorCode === ErrorCodes.RECAPTCHA_INVALID_TOKEN) {
        setGlobalErrorMessage({ title: 'global.error_title', message: 'sign_in_page.form.error.recaptcha' })
      } else if (errorCode === ErrorCodes.EXPIRED_SESSION) {
        setGlobalErrorMessage({ title: 'global.error_title', message: 'change_email.error_expired_session_error' })
      } else {
        const origin = searchParams.get(QueryParameters.ORIGIN)
        setGlobalErrorMessage({
          title: 'global.error_title',
          message: origin ? 'global.unknown_error_message_origin' : 'global.unknown_error_message',
        })
        console.error(`SignInPage: Unknown value for query param "${QueryParameters.ERROR_CODE}": ${errorCode}`)
        Sentry.captureMessage(`SignInPage: Unknown value for query param "${QueryParameters.ERROR_CODE}"`)
      }
    }
  }, [searchParams])

  useEffect(() => {
    hasStorageAccess().then((hasAccess: boolean) => setShowStorageAccessRequest(!hasAccess))
  }, [])

  return (
    <Layout>
      {showStorageAccessRequest ? (
        <StorageAccessRequestButton
          onSuccess={() => setShowStorageAccessRequest(false)}
          onError={setGlobalErrorMessage}
        />
      ) : showAuthenticationCodeForm ? (
        <MfaForm onSubmit={onMfaFormSubmit} error={showAuthenticationCodeForm} />
      ) : (
        <CredentialsForm onSubmit={onCredentialsFormSubmit} enableSso={enableSso} />
      )}

      {globalErrorMessage && <AlertError globalErrorMessage={globalErrorMessage} />}
    </Layout>
  )
}
